import React, { useState } from 'react';
import { Plus, Calendar, Clock, Trash2 , PencilIcon} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import CreateProjectModal from '../components/CreateProjectModal';
import ProjectContextDisplay from '../components/ProjectContextDisplay';
import DeletedProjectsBox from '../components/DeletedProjectsBox';
import toast from 'react-hot-toast';
import api from '../api';

const Dashboard: React.FC = () => {
  const { projects, setCurrentProject, updateProject, refreshProjects } = useProject();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [extendingProject, setExtendingProject] = useState<any>(null);
  const [newExpireDate, setNewExpireDate] = useState('');
  const [displayExtendDate, setDisplayExtendDate] = useState('');
  const [extendDateError, setExtendDateError] = useState('');
  const [projectToDelete, setProjectToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const extendDateInputRef = React.useRef<HTMLInputElement>(null);

  const handleProjectClick = (project: any) => {
    setCurrentProject(project);
    navigate('/app/sop-builder');
  };

  const calculateDaysRemaining = (expireDate: string) => {
    const today = new Date();
    const expire = new Date(expireDate);
    const diffTime = expire.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleExtendClick = (e: React.MouseEvent, project: any) => {
    e.stopPropagation();
    setExtendingProject(project);
    setNewExpireDate('');
    setDisplayExtendDate('');
    setExtendDateError('');
  };

  // Validate date
  const isValidDate = (day: number, month: number, year: number): boolean => {
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 2000 || year > 2100) return false;

    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    if (isLeapYear) daysInMonth[1] = 29;

    if (day > daysInMonth[month - 1]) return false;
    return true;
  };

  // Format extend date input
  const handleExtendDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const cleaned = input.replace(/[^\d/]/g, '');
    let formatted = cleaned;

    if (cleaned.length >= 2 && !cleaned.includes('/')) {
      formatted = cleaned.substring(0, 2) + '/' + cleaned.substring(2);
    }
    if (cleaned.length >= 5 && cleaned.split('/').length === 2) {
      const parts = cleaned.split('/');
      formatted = parts[0].substring(0, 2) + '/' + parts[1].substring(0, 2) + '/' + cleaned.substring(5);
    }

    const parts = formatted.split('/');
    if (parts[0] && parts[0].length > 2) {
      formatted = parts[0].substring(0, 2) + (parts[1] ? '/' + parts[1] : '');
    }
    if (parts[1] && parts[1].length > 2) {
      formatted = parts[0] + '/' + parts[1].substring(0, 2) + (parts[2] ? '/' + parts[2] : '');
    }
    if (parts[2] && parts[2].length > 4) {
      formatted = parts[0] + '/' + parts[1] + '/' + parts[2].substring(0, 4);
    }

    setDisplayExtendDate(formatted);

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(formatted)) {
      const [dd, mm, yyyy] = formatted.split('/').map(Number);

      if (isValidDate(dd, mm, yyyy)) {
        const selectedDate = new Date(yyyy, mm - 1, dd);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // For extend, calculate max date from current expiry date (not today)
        const currentExpiryDate = extendingProject?.expire_date ? new Date(extendingProject.expire_date) : today;
        currentExpiryDate.setHours(0, 0, 0, 0);

        const maxDate = new Date(currentExpiryDate);
        maxDate.setFullYear(maxDate.getFullYear() + 1);

        // Check if date is in the past
        if (selectedDate < today) {
          setNewExpireDate('');
          setExtendDateError('Expiry date cannot be in the past.');
        }
        // Check if date is more than 1 year from current expiry date
        else if (selectedDate.getTime() > maxDate.getTime()) {
          setNewExpireDate('');
          setExtendDateError('Expiry date cannot be more than 1 year from current expiry.');
        }
        else {
          const backendFormat = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
          setNewExpireDate(backendFormat);
          setExtendDateError('');
        }
      } else {
        setNewExpireDate('');
        setExtendDateError('Invalid date. Please check day, month, and year.');
      }
    } else {
      setNewExpireDate('');
      setExtendDateError('');
    }
  };

  const handleExtendCalendarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const [yyyy, mm, dd] = value.split('-');
      const selectedDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // For extend, calculate max date from current expiry date (not today)
      const currentExpiryDate = extendingProject?.expire_date ? new Date(extendingProject.expire_date) : today;
      currentExpiryDate.setHours(0, 0, 0, 0);

      const maxDate = new Date(currentExpiryDate);
      maxDate.setFullYear(maxDate.getFullYear() + 1);

      // Validate date range
      if (selectedDate < today) {
        setExtendDateError('Expiry date cannot be in the past.');
        setNewExpireDate('');
        setDisplayExtendDate('');
      } else if (selectedDate.getTime() > maxDate.getTime()) {
        setExtendDateError('Expiry date cannot be more than 1 year from current expiry.');
        setNewExpireDate('');
        setDisplayExtendDate('');
      } else {
        setDisplayExtendDate(`${dd}/${mm}/${yyyy}`);
        setNewExpireDate(value);
        setExtendDateError('');
      }
    }
  };

  const handleExtendSubmit = async () => {
    if (!extendingProject || !newExpireDate) return;

    try {
      const success = await updateProject(extendingProject.id, {
        expire_date: newExpireDate
      });

      if (success) {
        // Close modal and refresh projects
        setExtendingProject(null);
        setNewExpireDate('');
        setDisplayExtendDate('');
        setExtendDateError('');
        await refreshProjects();
      }
    } catch (error) {
      console.error('Failed to extend project:', error);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, project: any) => {
    e.stopPropagation(); // Prevent project card click
    setProjectToDelete(project);
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/projects/${projectToDelete.id}`);
      // Close modal and refresh projects
      toast.success("Project Deleted Successfully")
      setProjectToDelete(null);
      await refreshProjects();
    } catch (error: any) {
      console.error('Failed to delete project:', error);
      const message = error.response?.data?.error || 'Failed to delete project';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  // const auditTrail = [
  //   {
  //     id: 1,
  //     action: 'New RCSA project for banking client',
  //     user: 'User ID',
  //     timestamp: '6/13/2025, 3:04:22 AM'
  //   },
  //   {
  //     id: 2,
  //     action: 'New RCSA project for banking client',
  //     user: 'User ID',
  //     timestamp: '6/13/2025, 3:04:38 AM'
  //   }
  // ];

  return (
    <>
      <div className="space-y-6" data-tour="dashboard-content">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-super-dark-purple">Project/Documentation Repository</h1>
            <p className="text-gray-600">
              Welcome back! Here's an overview of your RCSA projects and activities.
            </p>
          </div>
          <button
            data-tour="new-project-button"
            onClick={() => setShowCreateModal(true)}
            className="bg-electric-blue text-white px-4 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Project</span>
          </button>
        </div>

        {/* Project Context Display */}
        <ProjectContextDisplay />

        {/* Deleted Projects Box */}
        <DeletedProjectsBox />

        {/* Project Overview */}
        <div className="grid grid-cols-1  gap-6">
          {/* All Projects */}
          <div className="lg:col-span-2 bg-white rounded-lg p-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-super-dark-purple">Most Recent Projects</h2>
            </div>

            <div className="space-y-4">
              {projects.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <Plus className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
                  <p className="text-gray-500 mb-4">Get started by creating your first RCSA project</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-electric-blue text-white px-4 py-2 rounded-lg hover:bg-darker-blue transition-colors inline-flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create First Project</span>
                  </button>
                </div>
              ) : (
                projects.slice(0, 5).map((project) => {
                  const daysRemaining = project.expire_date ? calculateDaysRemaining(project.expire_date) : null;
                  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 8;

                return (
                  <div
                    key={project.id}
                    className={`group relative border-2 rounded-xl transition-all duration-300 cursor-pointer hover:scale-[1.01] ${
                      isExpiringSoon
                        ? 'border-red-300 bg-gradient-to-br from-red-50 to-orange-50 hover:shadow-lg hover:shadow-red-200'
                        : 'border-electric-blue/20 bg-gradient-to-br from-electric-blue/5 to-blue-50 hover:shadow-lg hover:shadow-electric-blue/20'
                    }`}
                    onClick={() => handleProjectClick(project)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={e => { if (e.key === 'Enter') handleProjectClick(project); }}
                    title="Click to open this project in SOP Builder"
                  >
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-electric-blue text-white px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg pointer-events-none z-10">
                      Click to access SOP Builder →
                    </div>

                    {/* Warning/Status Banner */}
                    {project.expire_date && (
                      <div className={`px-4 py-2 rounded-t-xl text-sm font-semibold ${
                        isExpiringSoon
                          ? 'bg-red-500 text-white'
                          : 'bg-electric-blue text-white'
                      }`}>
                        {(() => {
                          const daysRemaining = calculateDaysRemaining(project.expire_date);
                          if (daysRemaining > 8) {
                            return `Normal - ${daysRemaining} days until deletion`;
                          } else if (daysRemaining > 0) {
                            return `⚠️ Warning - Only ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining until deletion`;
                          } else if (daysRemaining === 0) {
                            return `🚨 Critical - Project expires today!`;
                          } else {
                            return `🔥 Overdue - Project expired ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? '' : 's'} ago!`;
                          }
                        })()}
                      </div>
                    )}

                    <div className="p-6">
                      {/* Header Section */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1 pr-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-bold text-super-dark-purple underline decoration-electric-blue decoration-2 underline-offset-4 hover:decoration-4 transition-all cursor-pointer">
                            {project.name}
                          </h3>
                          <span className="bg-electric-blue text-white px-3 py-1 rounded-md text-xs font-medium shadow-sm">
                            Active
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed mb-3">
                          {project.description || 'No description provided'}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <span className="font-medium">Client:</span>
                            <span className="text-electric-blue font-semibold underline decoration-electric-blue/30 decoration-1 underline-offset-2">
                              {project.client_name || 'Sample Bank Corp'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>Modified: {
                              project.updated_at && !isNaN(new Date(project.updated_at).getTime())
                                ? new Date(project.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            }</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col space-y-2">
                        {project.expire_date && (
                          <button
                            onClick={(e) => handleExtendClick(e, project)}
                            className="group/btn px-4 py-2 bg-electric-blue text-white rounded-lg hover:bg-darker-blue transform transition-all shadow-md flex items-center space-x-2"
                          >
                            <PencilIcon className='w-4 h-4 ' />
                            <span className='text-sm font-medium'>Extend</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDeleteClick(e, project)}
                          className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg  transform 0 shadow-md hover:shadow-lg flex items-center space-x-2"
                          title="Delete Project"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className='text-sm font-medium'>Delete</span>
                        </button>
                      </div>
                    </div>

                    {/* Expiry Information */}
                    {project.expire_date && (
                      <div className={`p-3 rounded-lg border-l-4 ${
                        isExpiringSoon
                          ? 'bg-red-100 border-red-400'
                          : 'bg-electric-blue/10 border-electric-blue'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Clock className={`w-4 h-4 ${isExpiringSoon ? 'text-red-600' : 'text-electric-blue'}`} />
                            <span className={`text-sm font-medium ${isExpiringSoon ? 'text-red-800' : 'text-electric-blue'}`}>
                              {(() => {
                                const daysRemaining = calculateDaysRemaining(project.expire_date);
                                if (daysRemaining > 0) {
                                  return `Auto-delete in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
                                } else if (daysRemaining === 0) {
                                  return 'Expires today!';
                                } else {
                                  return `Expired ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? '' : 's'} ago`;
                                }
                              })()}
                            </span>
                          </div>
                          <span className={`text-sm font-semibold ${isExpiringSoon ? 'text-red-700' : 'text-electric-blue'}`}>
                            📅 {new Date(project.expire_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    )}

                    </div>
                  </div>
                );
              })
              )}
            </div>
          </div>
          <div className="space-y-6">
          </div>
        </div>
        {/* Current Project Stats */}
        
      </div>

      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Extend Expiry Modal */}
      {extendingProject && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 border border-gray-200">
            <h2 className="text-2xl font-bold text-super-dark-purple mb-4">
              Extend Project Expiry Date
            </h2>
            <p className="text-gray-600 mb-4">
              Extending expiry date for: <strong>{extendingProject.name}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Current expiry: {new Date(extendingProject.expire_date).toLocaleDateString()}
            </p>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  New Expiry Date (DD/MM/YYYY)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    ref={extendDateInputRef}
                    type="text"
                    value={displayExtendDate}
                    onChange={handleExtendDateInput}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                    className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent text-black ${
                      extendDateError ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <input
                    type="date"
                    id="calendar-picker-extend"
                    min={new Date().toISOString().split('T')[0]}
                    max={(() => {
                      const currentExpiry = extendingProject?.expire_date ? new Date(extendingProject.expire_date) : new Date();
                      currentExpiry.setHours(0, 0, 0, 0);
                      const maxDate = new Date(currentExpiry);
                      maxDate.setFullYear(maxDate.getFullYear() + 1);
                      return maxDate.toISOString().split('T')[0];
                    })()}
                    onChange={handleExtendCalendarSelect}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('calendar-picker-extend') as HTMLInputElement;
                      if (input) {
                        input.style.position = 'static';
                        input.style.opacity = '1';
                        input.style.pointerEvents = 'auto';
                        input.showPicker ? input.showPicker() : input.click();
                        setTimeout(() => {
                          input.style.position = 'absolute';
                          input.style.opacity = '0';
                          input.style.pointerEvents = 'none';
                        }, 100);
                      }
                    }}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Calendar className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                {extendDateError && (
                  <p className="text-sm text-red-600">{extendDateError}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setExtendingProject(null);
                    setNewExpireDate('');
                    setDisplayExtendDate('');
                    setExtendDateError('');
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendSubmit}
                  disabled={!newExpireDate}
                  className="px-5 py-2 rounded-lg bg-electric-blue text-white font-semibold hover:bg-darker-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Extend Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Project Confirmation Modal */}
      {projectToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 border border-gray-200">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Delete Project</h2>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete the project:
              </p>
              <p className="font-semibold text-gray-900 bg-gray-50 p-3 rounded-lg">
                {projectToDelete.name}
              </p>
              <p className="text-sm text-red-600 mt-3">
                ⚠️ This will permanently delete all project data including documents, analyses, process maps, and risk assessments.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setProjectToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProject}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Project</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;